import { toTtvDraggedPhoneme } from "@/app/(application)/(ttv)/ttv/analytics/toTtvDraggedPhoneme";
import type { TtvAnalyticsEvent } from "@/app/(application)/(ttv)/ttv/analytics/types";

// An aliased object can satisfy the type while carrying arbitrary extra keys, so every variant is rebuilt field by field — the same allowlist the stability trail enforces. Nothing here may carry free-text lyrics, note contents, audio, file names or project names: only values from fixed inventories (opaque project/singer/transfer/sound/conversion ids, voice ids from the static catalog, phoneme inventory tokens, pronunciation-dictionary words), enums, counts, booleans and durations.
export const sanitizeTtvEvent = (
  event: TtvAnalyticsEvent
): TtvAnalyticsEvent => {
  switch (event.event) {
    case "ttv_convert_shown":
      return {
        event: event.event,
        projectId: event.projectId,
      };
    case "ttv_convert_clicked":
      return {
        event: event.event,
        projectId: event.projectId,
      };
    case "ttv_convert_requested":
      return {
        event: event.event,
        projectId: event.projectId,
        singerId: event.singerId,
        transferId: event.transferId,
        takeCount: event.takeCount,
      };
    case "ttv_convert_failed":
      return {
        event: event.event,
        projectId: event.projectId,
        singerId: event.singerId,
        transferId: event.transferId,
        stage: event.stage,
      };
    case "ttv_convert_transferred":
      return {
        event: event.event,
        projectId: event.projectId,
        singerId: event.singerId,
        transferId: event.transferId,
        takes: event.takes.map((take) => ({
          soundId: take.soundId,
          takeIndex: take.takeIndex,
        })),
        vocalDurationSeconds: event.vocalDurationSeconds,
        transferMs: event.transferMs,
        saveMs: event.saveMs,
        renderMs: event.renderMs,
        uploadMs: event.uploadMs,
      };
    case "ttv_convert_input_ready":
      return {
        event: event.event,
        soundId: event.soundId,
        transferId: event.transferId,
      };
    case "conversion_completed":
      return {
        event: event.event,
        soundId: event.soundId,
        conversionId: event.conversionId,
        transferId: event.transferId,
      };

    case "ttv_session_started":
      return {
        event: event.event,
        daysSinceLastSession: event.daysSinceLastSession,
        projectCount: event.projectCount,
        hasPreviousTtvWork: event.hasPreviousTtvWork,
      };
    case "ttv_project_created":
      return {
        event: event.event,
        creationSource: event.creationSource,
        projectCountBefore: event.projectCountBefore,
        // Spread, not assigned: an unknown answer must leave the property OFF the record, not present holding undefined.
        ...(event.isFirstTtvProject === undefined ?
          {}
        : { isFirstTtvProject: event.isFirstTtvProject }),
      };
    case "ttv_project_reopened":
      return {
        event: event.event,
        reopenSource: event.reopenSource,
        projectAgeDays: event.projectAgeDays,
        daysSinceLastOpened: event.daysSinceLastOpened,
        projectNoteCount: event.projectNoteCount,
        rosterSingerCount: event.rosterSingerCount,
        hasBackingTrack: event.hasBackingTrack,
        bpm: event.bpm,
      };
    case "ttv_singer_picker_opened":
      return {
        event: event.event,
        openedFrom: event.openedFrom,
        rosterSingerCount: event.rosterSingerCount,
      };
    case "ttv_singer_selected":
      return {
        event: event.event,
        voiceId: event.voiceId,
        replacedVoiceId: event.replacedVoiceId,
        replacedSingerSlot: event.replacedSingerSlot,
        discoveryMethod: event.discoveryMethod,
        sortVariant: event.sortVariant,
        visibleListPosition: event.visibleListPosition,
        wasDefaultSingerReplaced: event.wasDefaultSingerReplaced,
      };
    case "ttv_singer_added":
      return {
        event: event.event,
        voiceId: event.voiceId,
        singerSlot: event.singerSlot,
        rosterSingerCount: event.rosterSingerCount,
        wasPartCopied: event.wasPartCopied,
      };
    case "ttv_singer_removed":
      return {
        event: event.event,
        voiceId: event.voiceId,
        singerSlot: event.singerSlot,
        rosterSingerCountAfter: event.rosterSingerCountAfter,
      };
    case "ttv_singer_focus_switched":
      return {
        event: event.event,
        previousVoiceId: event.previousVoiceId,
        previousSingerSlot: event.previousSingerSlot,
        newVoiceId: event.newVoiceId,
        newSingerSlot: event.newSingerSlot,
        rosterSingerCount: event.rosterSingerCount,
      };
    case "ttv_notes_entered":
      return {
        event: event.event,
        inputMethod: event.inputMethod,
        enteredNoteCount: event.enteredNoteCount,
      };
    case "ttv_backing_track_added":
      // Rebuilt per detection outcome as well as per event: a failed detection has no bpm and no source to carry, so the allowlist cannot smuggle one in.
      if (event.bpmDetection === "succeeded") {
        return {
          event: event.event,
          durationSeconds: event.durationSeconds,
          bpmDetection: event.bpmDetection,
          detectedBpm: event.detectedBpm,
          bpmSource: event.bpmSource,
        };
      }
      return {
        event: event.event,
        durationSeconds: event.durationSeconds,
        bpmDetection: event.bpmDetection,
      };
    case "ttv_generate_requested":
      return {
        event: event.event,
        intentSource: event.intentSource,
        resingScope: event.resingScope,
        resingNoteCount: event.resingNoteCount,
        runSingerCount: event.runSingerCount,
        varianceModelId: event.varianceModelId,
        varianceSteps: event.varianceSteps,
        acousticSteps: event.acousticSteps,
        // Voice ids from the static VOICES catalog — a product identifier, not user content. Copied rather than aliased so a later mutation of the caller's array cannot reach the retained record.
        rosterVoiceIds: [...event.rosterVoiceIds],
        rosterSingerCount: event.rosterSingerCount,
        unisonLayers: event.unisonLayers,
        backingTrackPresent: event.backingTrackPresent,
        projectNoteCount: event.projectNoteCount,
        runNoteCount: event.runNoteCount,
        hasPitchEditsThisSession: event.hasPitchEditsThisSession,
        notesManuallyEditedThisSession: event.notesManuallyEditedThisSession,
        meanVibrato: event.meanVibrato,
        meanDeVibrato: event.meanDeVibrato,
        meanVibratoRate: event.meanVibratoRate,
        harmonyPreset: event.harmonyPreset,
        effectsEnabled: event.effectsEnabled,
        effectsPreset: event.effectsPreset,
        effectsBrightness: event.effectsBrightness,
        effectsCompression: event.effectsCompression,
        effectsDeEsser: event.effectsDeEsser,
        effectsSaturation: event.effectsSaturation,
        effectsDelay: event.effectsDelay,
        effectsReverb: event.effectsReverb,
        effectsCompressionMode: event.effectsCompressionMode,
        effectsDelayNoteValue: event.effectsDelayNoteValue,
        effectsDelayStyle: event.effectsDelayStyle,
        effectsReverbMode: event.effectsReverbMode,
        effectsSaturationMode: event.effectsSaturationMode,
      };
    case "ttv_generate_succeeded":
      return {
        event: event.event,
        intentSource: event.intentSource,
        durationMs: event.durationMs,
        renderedAudioSeconds: event.renderedAudioSeconds,
        runSingerCount: event.runSingerCount,
        legCount: event.legCount,
        runVoiceIds: [...event.runVoiceIds],
      };
    case "ttv_generate_failed":
      return {
        event: event.event,
        intentSource: event.intentSource,
        durationMs: event.durationMs,
        errorType: event.errorType,
        runSingerCount: event.runSingerCount,
        legCount: event.legCount,
        runVoiceIds: [...event.runVoiceIds],
      };
    case "ttv_generate_cancelled":
      return {
        event: event.event,
        intentSource: event.intentSource,
        durationMs: event.durationMs,
        runSingerCount: event.runSingerCount,
        legCount: event.legCount,
      };
    case "ttv_unison_stack_built":
      return {
        event: event.event,
        previousTakeCount: event.previousTakeCount,
        requestedTakeCount: event.requestedTakeCount,
        renderedTakeCount: event.renderedTakeCount,
        durationMs: event.durationMs,
        stackOutcome: event.stackOutcome,
      };
    case "ttv_audio_render_failed":
      return {
        event: event.event,
        runKind: event.runKind,
        errorType: event.errorType,
        durationMs: event.durationMs,
      };
    case "ttv_playback_session":
      return {
        event: event.event,
        startPositionSeconds: event.startPositionSeconds,
        stopPositionSeconds: event.stopPositionSeconds,
        listenedSeconds: event.listenedSeconds,
        playableDurationSeconds: event.playableDurationSeconds,
        completionRatio: event.completionRatio,
        reachedEnd: event.reachedEnd,
        seekCount: event.seekCount,
        hadBackingTrack: event.hadBackingTrack,
        isFirstListenOfThisRender: event.isFirstListenOfThisRender,
      };
    case "ttv_control_changed":
      return {
        event: event.event,
        controlName: event.controlName,
        previousValue: event.previousValue,
        newValue: event.newValue,
        isFirstChangeThisSession: event.isFirstChangeThisSession,
      };
    case "ttv_preset_applied":
      // Rebuilt per preset TYPE as well as per event: an effects preset has no layer count and no launch surface, and must not carry harmony's fields.
      if (event.presetType === "effects") {
        return {
          event: event.event,
          presetType: event.presetType,
          presetName: event.presetName,
          previousPreset: event.previousPreset,
        };
      }
      return {
        event: event.event,
        presetType: event.presetType,
        presetName: event.presetName,
        previousPreset: event.previousPreset,
        layerCount: event.layerCount,
        launchedFrom: event.launchedFrom,
      };
    case "ttv_note_edited":
      return {
        event: event.event,
        editType: event.editType,
        editedNoteCount: event.editedNoteCount,
      };
    case "ttv_phoneme_timing_dragged":
      return {
        event: event.event,
        // The boundary re-derives the producer's value from the code alone: a label outside the inventory is free text, and the class is never taken on trust.
        phoneme:
          event.phoneme === null ?
            null
          : toTtvDraggedPhoneme(event.phoneme.code),
        word: event.word,
        voiceId: event.voiceId,
        deltaMs: event.deltaMs,
        isNoteStartHandle: event.isNoteStartHandle,
      };
    case "ttv_undo":
      return {
        event: event.event,
        undoneActionType: event.undoneActionType,
        undoStackDepth: event.undoStackDepth,
        hopCount: event.hopCount,
      };
    case "ttv_redo":
      return {
        event: event.event,
        redoneActionType: event.redoneActionType,
        redoStackDepth: event.redoStackDepth,
        hopCount: event.hopCount,
      };
  }
};
